import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { X } from '../../icons/lucide/X';
import { TABLET_MIN_WIDTH, usePlatformValue } from '../../platform';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { emojiSafeChildren } from '../../utils/emojiSafeChildren';
import { subTestID } from '../../utils/testId';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { Spinner } from '../Button/Spinner';
import { ProgressBar } from '../ProgressBar';
import {
  resolveToastDismissColor,
  resolveToastIconColor,
  TOAST_DISMISS_ICON_SIZE,
  TOAST_LEADING_ICON_SIZE,
} from './Toast.icons';
import { TOAST_NATIVE_SHADOW } from './Toast.shadows';
import type { ToastProps } from './Toast.types';
import {
  ACTION_BUTTON_SIZE,
  ACTION_BUTTON_VARIANT,
  BADGE_COLOUR,
  BADGE_SIZE,
  CONVERSATIONAL_BADGE_SIZE,
  CONVERSATIONAL_CONTENT_CLASS,
  CONVERSATIONAL_DESCRIPTION_CLASS,
  CONVERSATIONAL_GEOMETRY,
  CONVERSATIONAL_RADIUS_PX,
  CONVERSATIONAL_TABLET_MAX_WIDTH,
  TOAST_BORDER_WIDTH,
  TOAST_COPY_BASIS,
  TOAST_FILL_CLASS,
  TOAST_NARROW_CLASS,
  TOAST_RADIUS_PX,
  TOAST_STACKED_CLASS,
  toastDescriptionVariants,
  toastTitleVariants,
  toastVariants,
} from './Toast.variants';
import { ToastContentMorph } from './ToastContentMorph';
import { ToastGlassBackground } from './ToastGlassBackground';

/**
 * Cross-platform Toast card. Renders identically on web and React Native.
 *
 * Presentational only — no queue / auto-dismiss / portal / animation. Each
 * platform's toast engine (sonner / Radix on web, the native context provider
 * on mobile) renders this card and owns the lifecycle; `onAction` / `onDismiss`
 * are the wiring points.
 *
 * @example
 * ```tsx
 * import { Toast } from '@flashka/shared/ui';
 * import { CircleCheck } from '@flashka/shared/ui/icons';
 *
 * // Title only, dismissable
 * <Toast title="Saved" dismissable onDismiss={hide} />
 *
 * // Success with icon, description, and an action
 * <Toast
 *   variant="success"
 *   size="md"
 *   icon={CircleCheck}
 *   title="Deck restored"
 *   description="It's back in your library."
 *   actionLabel="Undo"
 *   onAction={undo}
 *   dismissable
 *   onDismiss={hide}
 * />
 * ```
 */
export function Toast({
  variant,
  size,
  title,
  description,
  icon: Icon,
  loading = false,
  progress,
  contentKey,
  leadingSlot,
  badgeLabel,
  badgeIcon: BadgeIcon,
  actionLabel,
  onAction,
  actions,
  layout,
  dismissable = false,
  onDismiss,
  dismissAccessibilityLabel,
  className,
  titleClassName,
  titleNumberOfLines,
  descriptionClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: ToastProps) {
  const { isDark } = useTheme();
  const scale = useResponsiveScale();
  // Phones, and browser windows no wider than one — see TOAST_NARROW_CLASS.
  const isNarrow = useBreakpoint().width < TABLET_MIN_WIDTH;
  const resolvedVariant = variant ?? 'default';
  const resolvedSize = size ?? 'sm';

  // The conversational variant owns its geometry (padding / width / radius) and
  // ignores the sm/md sizes. The Ka leading slot picks the presentation:
  // present → system fallback (symmetric); absent → in-chat bubble.
  const isConversational = resolvedVariant === 'conversational';
  const isKaFallback = isConversational && leadingSlot != null;
  // Native tablets widen the bubble's cap (see CONVERSATIONAL_TABLET_MAX_WIDTH).
  // Keyed by DEVICE (ipad / androidTablet), not the `tablet` form factor, so a
  // tablet-width BROWSER keeps Figma's 320 — on web the bubble is anchored to
  // the Ka launcher, not free-floating. `cn` is tailwind-merge, so the override
  // replaces the geometry's own `max-w-[320px]` rather than stacking with it.
  const tabletMaxWidth = usePlatformValue<string>({
    ipad: CONVERSATIONAL_TABLET_MAX_WIDTH,
    androidTablet: CONVERSATIONAL_TABLET_MAX_WIDTH,
  });
  // The stacked anatomy (see ToastProps.layout): visual → copy → controls, one
  // per line. `auto` asks for it only where the row cannot hold the card — a
  // narrow one, which is every phone toast — so a desktop toast column keeps
  // the Figma row. Conversational owns its geometry and ignores the knob.
  const isStacked =
    !isConversational && (layout === 'stacked' || (layout === 'auto' && isNarrow));

  const conversationalGeometry = isConversational
    ? cn(CONVERSATIONAL_GEOMETRY[isKaFallback ? 'fallback' : 'bubble'], tabletMaxWidth)
    : undefined;

  const containerClass = cn(
    toastVariants({
      variant: resolvedVariant,
      size: resolvedSize,
      // A leading VISUAL — the status icon, the loading spinner or a caller's
      // own slot (an avatar stack) — carries its own optical inset, so any one
      // of them tightens the padding. Conversational owns its geometry and opts
      // out entirely, and so does a STACKED card: there the visual sits ABOVE
      // the copy rather than beside it, so there is no double gap to correct
      // for and the size's own text padding is the right one.
      hasIcon:
        !isConversational &&
        !isStacked &&
        (Icon != null || loading || leadingSlot != null),
    }),
    conversationalGeometry,
    // The conversational bubble is one run of text with no trailing controls —
    // nothing to wrap, and it owns its own width. A stacked card has already
    // taken the row apart, so the narrow-card wrap has nothing left to decide.
    isStacked ? TOAST_STACKED_CLASS : isNarrow && !isConversational && TOAST_NARROW_CLASS,
    className,
  );

  // Hex on both platforms — the leading icon and dismiss X are static and each
  // a distinct colour from the title, so the Button `currentColor`-via-text
  // trick can't express them; resolve directly (see Toast.icons.ts).
  const iconColor = resolveToastIconColor(resolvedVariant, isDark);
  const dismissColor = resolveToastDismissColor(resolvedVariant, isDark);

  // Glass effect for every variant (Figma "Glass", radius 4) — conversational
  // included (product decision; its Figma nodes carry no glass, see
  // CONVERSATIONAL_GEOMETRY). Web renders it via the CVA's
  // `web:backdrop-blur-[6px]` (so this is a no-op there); native paints an
  // expo-blur BlurView clipped to the card radius on iOS, and an opaque card
  // base + the variant tint on Android, which cannot frost at all. The
  // variant's own translucent fill tints the frost. Mirrors the Button glass
  // pattern; strength comes from `styles/glass.ts`.
  //
  // The clip radius must track the card's ACTUAL rendered corner, which is two
  // things the raw Figma px isn't: rem-scaled (the `rounded-toast-*` tokens are
  // rem, so a tablet / OS-large-text renders them up to 1.25× bigger — see
  // `computeResponsiveRem`) and INSET by the 1px border, since RN lays an
  // `inset-0` absolute child out against the padding box. Using the unscaled
  // outer px squared the blur off over the card's rounded corner on iPad and
  // painted over the border — it read as a "cut" border at all four corners.
  const glassRadius = (px: number) => Math.max(0, px * scale - TOAST_BORDER_WIDTH);
  const glassBackground = (
    <ToastGlassBackground
      radius={glassRadius(
        isConversational
          ? CONVERSATIONAL_RADIUS_PX[isKaFallback ? 'fallback' : 'bubble']
          : TOAST_RADIUS_PX[resolvedSize],
      )}
      bottomRightRadius={
        isConversational && !isKaFallback
          ? glassRadius(CONVERSATIONAL_RADIUS_PX.bubbleTail)
          : undefined
      }
      isDark={isDark}
      fillClassName={TOAST_FILL_CLASS[resolvedVariant]}
    />
  );

  // Merge the native drop shadow into the caller's nativeProps.style. Web
  // ignores these RN style fields (the View primitive swallows nativeProps);
  // native renders the soft shadow per Figma. RN style array → later entries
  // win, so a caller's custom style still overrides where keys overlap.
  const mergedNativeProps = {
    ...(nativeProps ?? {}),
    style: [
      TOAST_NATIVE_SHADOW,
      (nativeProps as { style?: unknown } | undefined)?.style,
    ].filter(Boolean),
  };

  const hasDescription = description != null && description !== false;

  // Conversational content is one L3 run (its own typography) whether or not a
  // description follows it — the DS nodes spec exactly one type style for the
  // card. Every other variant uses the sm/md bold title matrix.
  const titleClass = isConversational
    ? cn(CONVERSATIONAL_CONTENT_CLASS, titleClassName)
    : cn(
        toastTitleVariants({ variant: resolvedVariant, size: resolvedSize }),
        titleClassName,
      );

  // `emojiSafeChildren`: on native, emoji runs escape the Mona Sans family
  // (which has no emoji glyphs) into the system font, INSIDE this styled
  // `<Text>` so the copy keeps the toast typography. No-op on web.
  const titleNode = isTextLike(title) ? (
    <Text
      testID={subTestID(testID, 'title')}
      className={titleClass}
      numberOfLines={titleNumberOfLines}>
      {emojiSafeChildren(title)}
    </Text>
  ) : (
    title
  );

  const descriptionClass = isConversational
    ? cn(CONVERSATIONAL_DESCRIPTION_CLASS, descriptionClassName)
    : cn(
        toastDescriptionVariants({ variant: resolvedVariant, size: resolvedSize }),
        descriptionClassName,
      );

  // A live counter takes the description's line (Figma `1034:19395`: title
  // over the bar) — the DS bar with its own percent label, in the toast's
  // primary hue.
  const hasProgress = !isConversational && progress != null;
  const descriptionNode = hasProgress ? (
    <ProgressBar value={progress} showLabel className="mt-0.5" />
  ) : !hasDescription ? null : isTextLike(description) ? (
    <Text testID={subTestID(testID, 'description')} className={descriptionClass}>
      {emojiSafeChildren(description)}
    </Text>
  ) : (
    description
  );

  // The leading visual: the caller's own slot if it passed one (an avatar, an
  // avatar stack — it owns its size), else the spinner while `loading`, else
  // the status icon. A caller passing several means the slot.
  //
  // Conversational: Ka (fallback presentation only) in a plain 24px
  // frame — CONVERSATIONAL_KA_SLOT_PX, per the in-line fallback node
  // (5856:49777), which carries no clip, radius or shadow — and NOTHING
  // otherwise. A conversational toast never shows a status icon: Ka is the
  // speaker, so a party-popper / gift glyph in that slot reads as a second,
  // competing subject. The `icon` prop is ignored for this variant rather than
  // trusted, because the rule kept getting broken one call site at a time.
  //
  // That frame is deliberately NOT `overflow-hidden`: the mascot is the
  // caller's element, and clipping turned a size mismatch into silently
  // cropped artwork instead of something obviously wrong.
  const leadingVisual = isConversational ? (
    leadingSlot ? (
      <View className="h-6 w-6 shrink-0">{leadingSlot}</View>
    ) : null
  ) : leadingSlot ? (
    <View className="shrink-0">{leadingSlot}</View>
  ) : loading ? (
    <View className="shrink-0">
      <Spinner color={iconColor} size={TOAST_LEADING_ICON_SIZE[resolvedSize]} />
    </View>
  ) : Icon ? (
    <View className="shrink-0">
      <Icon color={iconColor} size={TOAST_LEADING_ICON_SIZE[resolvedSize]} />
    </View>
  ) : null;

  // Trailing controls. Inline they are siblings of the copy in the card's own
  // row, each carrying its own 4px offset; stacked they share one final row and
  // its gap, so the offset would double the first one's inset.
  const trailingOffset = isStacked ? '' : 'ml-[4px]';

  // The toast's QUANTITY slot (streak, MP, multiplier, credits). Colour + size
  // come from the toast's own axes so the badge can't drift from the card it
  // sits in.
  const badgeNode =
    badgeLabel != null && badgeLabel !== false ? (
      <Badge
        type={BadgeIcon ? 'icon-label' : 'label'}
        colour={BADGE_COLOUR[resolvedVariant]}
        size={isConversational ? CONVERSATIONAL_BADGE_SIZE : BADGE_SIZE[resolvedSize]}
        icon={BadgeIcon}
        className={cn(trailingOffset, 'shrink-0')}>
        {badgeLabel}
      </Badge>
    ) : null;

  const actionNode = actionLabel ? (
    <Button
      variant={ACTION_BUTTON_VARIANT[resolvedVariant]}
      size={ACTION_BUTTON_SIZE[resolvedSize]}
      onPress={onAction}
      testID={subTestID(testID, 'action')}
      className={cn(trailingOffset, 'shrink-0')}>
      {actionLabel}
    </Button>
  ) : actions ? (
    // Caller-owned control. Mutually exclusive with `actionLabel` so the
    // trailing slot always has exactly one occupant.
    <View className={cn(trailingOffset, 'shrink-0')}>{actions}</View>
  ) : null;

  const dismissNode = dismissable ? (
    <Pressable
      as="div"
      onPress={onDismiss}
      accessibilityLabel={dismissAccessibilityLabel ?? 'Dismiss'}
      accessibilityRole="button"
      testID={subTestID(testID, 'close')}
      className={cn(
        trailingOffset,
        'shrink-0 web:transition-opacity web:hover:opacity-70',
      )}>
      <X color={dismissColor} size={TOAST_DISMISS_ICON_SIZE[resolvedSize]} />
    </Pressable>
  ) : null;

  const copyNode = (
    <>
      {titleNode}
      {descriptionNode}
    </>
  );

  // The inline row's visual+copy group (see the JSX below for why it is one
  // flex item). Defined once so the morphing and the plain render agree.
  const copyGroupClass = cn(
    'flex-row items-center gap-[8px] min-w-0 grow shrink',
    isConversational ? 'basis-auto' : TOAST_COPY_BASIS[resolvedSize],
  );

  return (
    <View
      className={containerClass}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={webProps}
      nativeProps={mergedNativeProps}>
      {glassBackground}

      {isStacked ? (
        <>
          {contentKey != null ? (
            <ToastContentMorph morphKey={contentKey} className="w-full">
              {leadingVisual}
              <View className="w-full">{copyNode}</View>
            </ToastContentMorph>
          ) : (
            <>
              {leadingVisual}
              {/* `w-full`: the copy owns the whole card width here. Without it the
                  column hugs its longest word on native, which is the crushed
                  one-word-per-line card the stacked layout exists to end. No
                  `min-w-0` beside it — a definite width has no min-content floor
                  to clear, and on the cross axis there was never one to begin
                  with (see View/min-width-shrink.spec). */}
              <View className="w-full">{copyNode}</View>
            </>
          )}
          {badgeNode || actionNode || dismissNode ? (
            <View className="flex-row items-center gap-[8px]">
              {badgeNode}
              {actionNode}
              {dismissNode}
            </View>
          ) : null}
        </>
      ) : (
        <>
          {/* The leading visual and the copy travel TOGETHER — one flex item, so
              a card too narrow for the whole row can only break between the copy
              and the trailing controls. Left as siblings they were separable, and
              on a really narrow card the copy jumped to its own line and left the
              avatar sitting alone above it. The group carries the basis the wrap
              decision reads; inside it nothing wraps.

              With a `contentKey` that group is also the morphing part of the
              card: a re-emit that changes the key crossfades (blur + fade)
              instead of snapping — "Uploading X…" turning into "X uploaded". */}
          {contentKey != null ? (
            <ToastContentMorph
              morphKey={contentKey}
              className={cn('flex', copyGroupClass)}>
              {leadingVisual}
              <View className="min-w-0 grow shrink">{copyNode}</View>
            </ToastContentMorph>
          ) : (
            <View className={copyGroupClass}>
              {leadingVisual}
              {/* The copy column. `grow shrink` rather than `flex-auto`: the `flex`
                  shorthand would reset the basis the group above sets, because
                  Tailwind emits `flex` after `flex-basis`. `min-w-0` lets a card too
                  narrow for the basis shrink this column instead of overflowing. */}
              <View className="min-w-0 grow shrink">{copyNode}</View>
            </View>
          )}
          {badgeNode}
          {actionNode}
          {dismissNode}
        </>
      )}
    </View>
  );
}

Toast.displayName = 'Toast';

/**
 * String / number / array-of-those render inside a styled `<Text>` (required
 * on native). Any other JSX is rendered as-is — the caller owns its styling.
 */
function isTextLike(node: unknown): boolean {
  if (node == null || typeof node === 'boolean') return false;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isTextLike);
  return false;
}
